import React from 'react';
import { t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Paragraph } from '@rover/kibble/core';
import FirstFAQIsOpen from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FirstFAQIsOpen';
import FrequentlyAskedQuestion from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FrequentlyAskedQuestion';

type HouseSittingServiceFaqsProps = {
  htmlRef?: React.RefObject<HTMLElement>;
};

const HouseSittingServiceFaqs: React.FC<HouseSittingServiceFaqsProps> = ({ htmlRef }) => {
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="house-sitting-service-faqs"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>House sitting near you</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>
          Discover personalized pet care with a loving pet sitter who can stay overnight in your
          home.
        </Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`How does house sitting work through Rover?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              House sitting on Rover connects you with local pet sitters who'll stay in your home to
              care for your pets and keep an eye on the property. Search and compare reliable house
              sitters near you by reading detailed reviews from repeat clients. Message your ideal
              sitter and meet them to see how they interact with your pets, and go over care
              instructions. Once you're satisfied, it's time to book! Rover makes it easy and
              protects your personal information with encryption. Depending on what you're looking
              for, your sitter can stay overnight with your pets or drop in at times you choose to
              look after your pets and home.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        <FrequentlyAskedQuestion
          question={t`How do I verify the background of a potential house sitter?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              All sitters joining Rover must pass a background check before you can book with them.
              There are two kinds of third-party background checks sitters can take: a basic
              verified background check and an enhanced background check. Sitters will have either a
              blue or gold shield badge on their profile, depending on which background check they
              have passed. For extra peace of mind, read verified reviews from other pet parents and
              repeat client stats. For more details, see Rover's Trust & Safety page.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`What do house sitters usually do?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              A house sitter's primary responsibility is caring for your pets, followed by any other
              household tasks they have agreed to and discussed with you. Typical pet sitting duties
              include feeding and playing with pets, keeping their area clean, keeping water fresh,
              and providing updates to their parents. Home tasks may include things like watering
              plants and collecting mail. These additional home tasks can be worked out during your
              Meet & Greet. It's a good idea to write out the list you both agreed to and send it as
              a confirmation in-app before the house sitting service is booked and paid for.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        <FrequentlyAskedQuestion question={t`How much do house sitters charge per day?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              The average cost for house sitters ranges from $45 to $75 per night, depending on the
              number of pets, task expectations, and sitter experience. Some house sitters may offer
              bundled rates for extended stays or charge more for additional services like
              medication administration, care for multiple pets, or yard work, such as watering
              plants.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion question={t`Are house sitters required to stay overnight?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Many homeowners searching for &quot;house sitters near me&quot; prefer sitters who
              stay overnight to maximize the security of their home and the comfort of their pets.
              However, not all sitters have their overnight rates listed. If you expect a sitter to
              stay overnight, mention this in your initial message. Overnight or 24/7 care
              expectations may change the nightly rate.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion question={t`How do I find a reputable house sitter in my area?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              On Rover, filter sitters by pet needs, location, and availability. Check the sitter's
              profile for repeat clients, recent reviews, and background checks.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={t`What kind of reviews or ratings should I look for on a sitter's profile?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Look for sitters with consistently high ratings and detailed reviews from pet parents
              whose needs are similar to yours. Prioritize feedback that highlights a sitter's
              communication skills, trustworthiness, and quality of care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={t`How do I make sure there is clear communication with a house sitter regarding expectations?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              To avoid misunderstandings, outline your house sitting expectations through Rover's
              messaging tools. Provide written documentation for pet care instructions, care
              schedules, and any additional responsibilities. Ask the sitter to confirm their
              understanding and request regular updates and photos. Open communication leads to
              peace of mind and a better house-sitting experience.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        <FrequentlyAskedQuestion question={t`What are the alternatives to hiring a house sitter?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              If you decide not to hire a house sitter and still need pet care, consider dog
              boarding (overnight care in a sitter's home) or multiple drop-ins (daytime care
              without overnight requirements). Multiple drop-ins are great for cats who like to stay
              at home but need play and socialization time.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default HouseSittingServiceFaqs;
